import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import "../usermgmt-filter.scss";
import { Formik, Form } from "formik";
import "./filter-options-usermgmt.scss";
import { userMngmtFilterOptions } from "../../user-management-list.constant";
import { camelCase } from "lodash";
import CompanySelect from "../../components/company-select/company-select";
import RoleSelect from "../../components/role-select/role-select";
import StatusFilter from "../../../../tenant/tenant-list/tenant-filter-options/status-filter";
import OnboardedDateFilter from "../lead-filter-options/onboardeddate-filter";
import FirstNameFilter from "../lead-filter-options/first-name-filter";
import LastNameFilter from "../lead-filter-options/last-name-filter";
import EmailFilter from "../../../../tenant/tenant-list/tenant-filter-options/email-filter";
import { useLogin } from "../../../../../common/hooks/useUser";
import dayjs from "dayjs";

const UserManagementOptions: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
  companyNames?: any;
  roles?: any;
}> = ({ open, onClose, onApplyFilters, filters, companyNames, roles }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    userMngmtFilterOptions.map((opt) => ({ ...opt }))
  );

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Date Added Range") {
            setFieldValue("onBoardedAtStart", null);
            setFieldValue("onBoardedAtEnd", null);
          } else if (label === "Company Name") {
            setFieldValue("company", "");
          } else if (label === "Roles") {
            setFieldValue("role", []);
          } else {
            const key = camelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);
  const tenantDetails = useLogin();
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Date Added Range") {
        values["onBoardedAtStart"] = null;
        values["onBoardedAtEnd"] = null;
      } else if (option.label === "Company Name") {
        values["company"] = "";
      } else if (option.label === "Roles") {
        values["role"] = [];
      } else {
        const key = camelCase(option.label);
        if (option.label === "Status") {
          values[key] = [];
        } else {
          values[key] = "";
        }
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Date Added Range") {
            acc["onBoardedAtStart"] = values["onBoardedAtStart"];
            acc["onBoardedAtEnd"] = values["onBoardedAtEnd"];
          } else if (curr.label === "Company Name") {
            acc["company"] = values["company"];
          } else if (curr.label === "Roles") {
            acc["role"] = values["role"];
          } else {
            const key = camelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");
    filterOptions.forEach((option) => {
      if (option.value) {
        if (option.label === "Date Added Range") {
          const startDate = values["onBoardedAtStart"]
            ? dayjs(values["onBoardedAtStart"])
            : null;
          const endDate = values["onBoardedAtEnd"]
            ? dayjs(values["onBoardedAtEnd"])
            : null;
          if (!startDate) {
            errors["onBoardedAtStart"] = "Start date is required";
          } else if (startDate.isBefore(minDate)) {
            errors["onBoardedAtStart"] = "Invalid date";
          }

          if (!endDate) {
            errors["onBoardedAtEnd"] = "End date is required";
          } else if (endDate.isBefore(minDate)) {
            errors["onBoardedAtEnd"] = "Invalid date";
          }
        } else if (option.label === "Company Name") {
          if (!values.company) {
            errors["company"] = "Company is required";
          }
        } else if (option.label === "Roles") {
          if (
            !values.role ||
            (Array.isArray(values.role) && values.role.length === 0)
          ) {
            errors["role"] = "Role is required";
          }
        } else {
          const key = camelCase(option.label);
          if (
            !values[key] ||
            (Array.isArray(values[key]) && values[key].length === 0)
          ) {
            errors[key] = `${option.label} cannot be empty`;
          }
        }
      }
    });
    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void
  ) => {
    switch (label) {
      case "Company Name":
        return isSuperParter ? (
          <CompanySelect
            value={values.company}
            onChange={(option) => setFieldValue?.("company", option)}
            companyNames={companyNames}
            touched={!!touched?.company}
            error={errors?.company}
          />
        ) : null;
      case "Email":
        return (
          <EmailFilter
            isEmailEnabled={true}
            value={values["email"]}
            error={errors?.email}
            onChange={(e: any) => setFieldValue?.("email", e.target.value)}
          />
        );
      case "Roles":
        return (
          <RoleSelect
            value={values.role || []}
            onChange={(option) => setFieldValue?.("role", option)}
            roles={roles || []}
            touched={!!touched?.role}
            error={errors?.role}
            isMultiple
          />
        );
      case "Status":
        return (
          <StatusFilter
            isStatusEnabled={true}
            value={values["status"] || []}
            error={errors?.status}
            onChange={(val) => setFieldValue?.("status", val)}
          />
        );
      case "Date Added Range":
        return (
          <OnboardedDateFilter
            startDate={values.onBoardedAtStart}
            endDate={values.onBoardedAtEnd}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("onBoardedAtStart", start);
              if (end) setFieldValue?.("onBoardedAtEnd", end);
            }}
            startDateError={
              touched?.onBoardedAtStart && errors?.onBoardedAtStart
                ? String(errors.onBoardedAtStart)
                : undefined
            }
            endDateError={
              touched?.onBoardedAtEnd && errors?.onBoardedAtEnd
                ? String(errors.onBoardedAtEnd)
                : undefined
            }
          />
        );
      case "First Name":
        return (
          <FirstNameFilter
            isFirstNameEnabled={true}
            value={values["firstName"]}
            error={errors?.lastName}
            onChange={(e: any) => setFieldValue?.("firstName", e.target.value)}
          />
        );
      case "Last Name":
        return (
          <LastNameFilter
            isLastNameEnabled={true}
            value={values["lastName"]}
            error={errors?.lastName}
            onChange={(e: any) => setFieldValue?.("lastName", e.target.value)}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const updatedOptions = filterOptions.map((opt) => {
      let key: string;

      switch (opt.label) {
        case "Company Name":
          key = "company";
          break;
        case "Roles":
          key = "role";
          break;
        case "Date Added Range":
          key =
            "onBoardedAtStart" in filters || "onBoardedAtEnd" in filters
              ? "dateRange"
              : "";
          break;
        default:
          key = camelCase(opt.label);
      }

      const isSelected =
        opt.label === "Date Added Range"
          ? "onBoardedAtStart" in filters || "onBoardedAtEnd" in filters
          : key in filters;

      return { ...opt, value: !!isSelected };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`filter-drawer-usermgmt ${open ? "open" : ""}`}>
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon fontSize="small" onClick={onClose} />
        </div>
        <h4>Filter By Fields</h4>
      </div>

      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
        }) => {
          return (
            <Form className="drawer-content-user-mgmt" onSubmit={handleSubmit}>
              {filterOptions.map((option) =>
                option.label === "Company Name" && !isSuperParter ? null : (
                  <div key={option.label} className="filter-option-row">
                    <MenuItem
                      key={option.label}
                      value={option.label}
                      className="filter-menu-item"
                      onClick={() =>
                        handleCheckboxChange(
                          option.label,
                          values,
                          setFieldValue
                        )
                      }
                    >
                      <Checkbox checked={option.value} />
                      <ListItemText primary={option.label} />
                    </MenuItem>

                    {option.value &&
                      renderFilterField(
                        option.label,
                        values,
                        errors,
                        touched,
                        setFieldValue
                      )}
                  </div>
                )
              )}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        userMngmtFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({});
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default UserManagementOptions;
